import { useEffect, useId, useRef, useState } from 'react';

import { Button, Card, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { EVENTO_DO_LOGO } from '@/comuns/logoDaClinica';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';

/**
 * O logotipo da clínica — trilha T6, migração 0098.
 *
 * Balde público `logos`, porque logotipo não é dado sensível e precisa aparecer
 * sem URL que vence. Só PNG, JPEG ou WebP até 2 MB; só quem tem a permissão de
 * configurações envia. Sem logotipo, vale a marca padrão.
 */
export function LogotipoDaClinica() {
    const aviso = useAviso();
    const idDoArquivo = useId();
    const entrada = useRef<HTMLInputElement | null>(null);
    const podeTrocar = pode('configuracoes');
    const [logo, setLogo] = useState<string | null | undefined>(undefined);
    const [erro, setErro] = useState<string | null>(null);
    const [enviando, setEnviando] = useState(false);
    const [removendo, setRemovendo] = useState(false);

    useEffect(() => {
        let vivo = true;
        dados().logoDaClinica()
            .then((url) => { if (vivo) setLogo(url); })
            .catch((e: unknown) => {
                if (!vivo) return;
                setLogo(null);
                setErro(e instanceof Error ? e.message : 'Não foi possível ler o logotipo.');
            });
        return () => { vivo = false; };
    }, []);

    const enviar = async (arquivo: File | undefined) => {
        if (!arquivo) return;
        setEnviando(true);
        setErro(null);
        try {
            setLogo(await dados().enviarLogoDaClinica(arquivo));
            window.dispatchEvent(new Event(EVENTO_DO_LOGO));
            aviso({ title: 'Logotipo enviado', description: arquivo.name });
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível enviar o logotipo.');
        } finally {
            setEnviando(false);
            if (entrada.current) entrada.current.value = '';
        }
    };

    const remover = async () => {
        setEnviando(true);
        setErro(null);
        try {
            await dados().removerLogoDaClinica();
            setLogo(null);
            setRemovendo(false);
            window.dispatchEvent(new Event(EVENTO_DO_LOGO));
            aviso({ title: 'De volta à marca padrão' });
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível remover o logotipo.');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Card title="Logotipo" subtitle="Aparece no topo do aplicativo. PNG, JPEG ou WebP, até 2 MB." padding={18}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', background: 'var(--surface-secondary)', overflow: 'hidden' }}>
                        {logo
                            ? <img src={logo} alt="Logotipo atual da clínica" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            : <Icon name="building-2" size={22} style={{ color: 'var(--neutral-500)' }} />}
                    </div>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {logo === undefined ? 'Lendo o logotipo…' : logo ? 'Logotipo da clínica' : 'Sem logotipo: vale a marca padrão.'}
                    </span>
                </div>
                {podeTrocar ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <label htmlFor={idDoArquivo} className="vec-sr-only">Arquivo do logotipo</label>
                        <input
                            id={idDoArquivo} ref={entrada} type="file" accept="image/png,image/jpeg,image/webp"
                            className="vec-sr-only" onChange={(e) => { void enviar(e.target.files?.[0]); }}
                        />
                        <Button size="sm" loading={enviando} iconLeft={<Icon name="upload" size={14} />} onClick={() => entrada.current?.click()}>
                            {logo ? 'Trocar logotipo' : 'Enviar logotipo'}
                        </Button>
                        {logo && (
                            <Button size="sm" variant="ghost" disabled={enviando} onClick={() => setRemovendo(true)}>
                                Voltar à marca padrão
                            </Button>
                        )}
                    </div>
                ) : (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                        Só quem tem a permissão de configurações troca o logotipo.
                    </p>
                )}
            </div>
            <Modal
                open={removendo} onClose={() => setRemovendo(false)} width={440} tone="danger" title="Voltar à marca padrão?"
                description="O logotipo sai do topo do aplicativo. Para voltar a usá-lo, é preciso enviar de novo."
                footer={(
                    <>
                        <Button variant="secondary" onClick={() => setRemovendo(false)}>Cancelar</Button>
                        <Button variant="danger" loading={enviando} onClick={() => { void remover(); }}>Remover</Button>
                    </>
                )}
            />
        </Card>
    );
}
